/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { diag } from '@opentelemetry/api';
import { getStringFromEnv } from '@opentelemetry/core';
import * as fs from 'fs';
import * as yaml from 'yaml';
import type { ConfigFactory } from './IConfigFactory';
import { substituteEnvVars } from './utils';
import type {
  AttributeNameValue,
  ConfigurationModel,
  TextMapPropagator,
} from './generated/types';
// Pre-compiled ajv validator — eliminates runtime schema compilation on cold start.
// Generated by `npm run generate:config`; do not import ajv directly here.
import * as validateConfig from './generated/validator';

export class FileConfigFactory implements ConfigFactory {
  private _config: ConfigurationModel;

  constructor() {
    this._config = parseConfigFile();
  }

  getConfigModel(): ConfigurationModel {
    return this._config;
  }
}

// The schema version this SDK targets. Per the configuration versioning spec,
// an implementation MUST reject a config file whose MAJOR version differs (a
// breaking-change boundary), accepts any MINOR version within that major, and
// SHOULD warn when the MINOR version is newer than it targets since unknown
// settings may be ignored. See
// https://github.com/open-telemetry/opentelemetry-configuration/blob/main/VERSIONING.md
const SUPPORTED_FILE_FORMAT_MAJOR = 1;
const SUPPORTED_FILE_FORMAT_MINOR = 1;

function validateFileFormat(configFile: string, fileFormat: unknown): void {
  if (typeof fileFormat !== 'string' || fileFormat.length === 0) {
    throw new Error(
      `${configFile}: Unsupported file_format: "${fileFormat}". Expected a "MAJOR.MINOR" version string.`
    );
  }

  // Drop any pre-release / meta tag, e.g. "1.0-rc.2" -> "1.0".
  const parts = fileFormat.split('-', 1)[0].split('.');
  const major = Number(parts[0]);
  const minor = parts.length > 1 ? Number(parts[1]) : 0;
  if (!Number.isInteger(major) || !Number.isInteger(minor)) {
    throw new Error(
      `${configFile}: Unsupported file_format: "${fileFormat}". Expected "MAJOR.MINOR" version numbers.`
    );
  }

  if (major !== SUPPORTED_FILE_FORMAT_MAJOR) {
    throw new Error(
      `${configFile}: Unsupported file_format: "${fileFormat}". This SDK supports schema version ${SUPPORTED_FILE_FORMAT_MAJOR}.x.`
    );
  }

  if (minor > SUPPORTED_FILE_FORMAT_MINOR) {
    diag.warn(
      `${configFile}: Configuration file_format "${fileFormat}" has a newer minor version than this SDK supports (${SUPPORTED_FILE_FORMAT_MAJOR}.${SUPPORTED_FILE_FORMAT_MINOR}); some settings may be ignored.`
    );
  }
}

export function parseConfigFile(): ConfigurationModel {
  const configFile = getStringFromEnv('OTEL_CONFIG_FILE') || '';
  const file = fs.readFileSync(configFile, 'utf8');

  const doc = yaml.parseDocument(file, { version: '1.2' });
  substituteEnvVars(doc);
  const processed = doc.toJS() as Record<string, unknown>;

  validateFileFormat(configFile, processed?.file_format);

  const valid = validateConfig(processed);
  if (!valid) {
    let detail: string;
    if (!validateConfig.errors) {
      detail = 'unknown error';
    } else if (validateConfig.errors.length === 1) {
      const err = validateConfig.errors[0];
      detail = `${err.instancePath} ${err.message}`;
    } else {
      const sep = '\n  ';
      detail =
        sep +
        validateConfig.errors
          .map(e => `${e.instancePath} ${e.message}`)
          .join(sep);
    }
    throw new Error(
      `Invalid OpenTelemetry config file: ${configFile}: ${detail}`
    );
  }

  const data = processed as unknown as ConfigurationModel;

  // Strip file_format from output — it's a meta-field, not a config value
  delete (data as Record<string, unknown>)['file_format'];

  return data;
}

/**
 * Merge attributes from `resource.attributes` and `resource.attributes_list`
 * (comma-separated key=value pairs) into an `AttributeNameValue[]` array.
 * Entries already in `resource.attributes` taking precedence.
 *
 * Note: The returned array might not be a copy, so it should not be mutated.
 */
export function mergeResourceAttributesConfig(
  attributes?: AttributeNameValue[],
  attributes_list?: string | null
): AttributeNameValue[] | undefined {
  if (!attributes_list) {
    return attributes;
  }

  const mergedAttrs = attributes ? attributes.slice() : [];
  const existingNames = new Set(mergedAttrs.map(a => a.name));

  // TODO(6842): handle attribute limits, if any
  let discard = false;
  const listAttrs: Record<string, string> = {};
  for (const pair of attributes_list.split(',')) {
    if (pair.trim() === '') continue;

    const parts = pair.split('=');
    if (parts.length !== 2) {
      diag.warn(
        `Invalid format for resource.attributes_list entry "${pair}": expected key=value with '=' percent-encoded in keys/values. Discarding all entries.`
      );
      discard = true;
      break;
    }

    const rawName = parts[0].trim();
    const rawValue = parts[1].trim();
    if (rawName === '') {
      diag.warn(
        `Empty attribute name in resource.attributes_list entry "${pair}". Discarding all entries.`
      );
      discard = true;
      break;
    }

    try {
      listAttrs[decodeURIComponent(rawName)] = decodeURIComponent(rawValue);
    } catch (e) {
      diag.warn(
        `Failed to percent-decode resource.attributes_list entry "${pair}", discarding all entries: ${e}`
      );
      discard = true;
      break;
    }
  }

  if (!discard) {
    for (const [name, value] of Object.entries(listAttrs)) {
      if (!existingNames.has(name)) {
        mergedAttrs.push({ name, value, type: 'string' });
      }
    }
  }

  return mergedAttrs;
}

/**
 * Merge propagator.composite_list (comma-separated propagator names) into
 * propagator.composite, with entries already in composite taking precedence.
 * Merge TextMapPropagator configs from `propagator.composite` and `propagator.composite_list`
 * (comma-separated names) into a `TextMapPropagator[]` array.
 * Entries already in `propagator.composite` taking precedence.
 *
 * Note: The returned array might not be a copy, so it should not be mutated.
 */
export function mergePropagatorCompositeConfig(
  composite?: TextMapPropagator[],
  composite_list?: string | null
): TextMapPropagator[] | undefined {
  if (!composite_list) {
    return composite;
  }

  const mergedComposite = composite ? composite.slice() : [];
  const existingNames = new Set(mergedComposite.map(c => Object.keys(c)[0]));

  for (const name of composite_list.split(',')) {
    const trimmed = name.trim();
    if (trimmed && !existingNames.has(trimmed)) {
      mergedComposite.push({ [trimmed]: null });
      existingNames.add(trimmed);
    }
  }

  return mergedComposite;
}
